import {
  useAtomValue,
  useAtom,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  currentFlowTag,
  currentFolderData,
  currentStatusAtom,
  presentationFolderAtom,
  presentationFolderResetAtom,
} from './atoms'
import {
  groupFilesByOwner,
  RootFolderId,
  startFilesForFolderJob,
  transferFileOwnership,
} from './utility'
import { useCallback, useEffect, useMemo } from 'react'
import { googleUserAtom } from 'infrastructure/googleDrive'
import { deleteFlow } from '../contentService/utility'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import {
  ContentServiceFlowStatus,
  GDriveFile,
  RequestError,
} from '../contentService/types'
import { useUrlSearchParam } from 'shared/hooks'
import { openTab } from '../Helpers'
import { GDriveFileInfo } from 'infrastructure/googleDrive'

const TRANSFER_TO_EMAIL = 'curriculum.team@kiddom.co'
const TRANSFER_FLOW_PARAM_KEY = 'transferFlowId'

export function useCurrentFolderInfo() {
  const [folderId, setFolderId] = useAtom(presentationFolderAtom)
  const folderInfo = useAtomValue(currentFolderData) as GDriveFileInfo

  return useMemo(() => {
    return {
      folderId,
      isRoot: folderId === RootFolderId,
      name: folderInfo?.name ?? '',
      setId: setFolderId,
      setToRoot: () => setFolderId(RootFolderId),
      isFolder: folderInfo?.mimeType === 'application/vnd.google-apps.folder',
    }
  }, [folderId, folderInfo, setFolderId])
}

export function useCreatePresentationJob() {
  const gAuth = useAtomValue(googleUserAtom)
  const folderId = useAtomValue(presentationFolderAtom)
  const flowTag = useAtomValue(currentFlowTag)
  const setVersion = useSetAtom(presentationFolderResetAtom)

  return useCallback(async () => {
    if (!gAuth.credential?.accessToken || !flowTag) return
    await startFilesForFolderJob(folderId, gAuth.credential.accessToken)
    setVersion((prev) => prev + 1)
  }, [setVersion, gAuth, folderId, flowTag])
}

export function useDeletePresentationJob() {
  const setVersion = useSetAtom(presentationFolderResetAtom)
  const flowTag = useAtomValue(currentFlowTag)
  const dataSetter = useSetStatus()
  return useCallback(async () => {
    if (!flowTag) return
    await deleteFlow(flowTag)
    dataSetter(undefined)
    setVersion((prev) => prev + 1)
  }, [flowTag, dataSetter, setVersion])
}

export function useSetStatus() {
  const setter = useSetAtom(currentStatusAtom)

  return useCallback(
    (status?: ContentServiceFlowStatus | RequestError) => {
      setter({ status })
    },
    [setter],
  )
}

export function useTransferOwnershipWithErrorNotification() {
  return useWithErrorNotification(
    { errorTitle: 'Error transferring ownership' },
    transferFileOwnership,
    (resp) => {
      const errs = [resp.error]
      const details = (resp as any).data?.details
      if (Array.isArray(details)) {
        errs.push(...details)
      }
      return errs.filter(Boolean) as string[]
    },
  )
}

export function useTransferOwnership(gDriveFiles: GDriveFile[]) {
  const gAuth = useAtomValue(googleUserAtom)
  const doTransfer = useTransferOwnershipWithErrorNotification()
  const [flowId, setFlowId] = useTransferJobFlowId()
  return useMemo(() => {
    const { ownerFiles } = groupFilesByOwner(gDriveFiles)
    const { email, name } = gAuth.additionalUserInfo?.profile ?? ({} as any)
    const userFiles = ownerFiles[email] ?? []
    return {
      transferTo: TRANSFER_TO_EMAIL,
      currentUser: { name, email },
      userFileCount: userFiles.length,
      canTransfer:
        !flowId && !!gAuth.credential?.accessToken && !!userFiles.length,
      doTransfer: async () => {
        if (!gAuth.credential?.accessToken) return
        const result = await doTransfer(gAuth.credential?.accessToken, {
          new_owner_email: TRANSFER_TO_EMAIL,
          file_ids: userFiles.map(({ id }) => id) ?? [],
        })
        if (!result) return
        const { flow_id } = result.data
        openTab('/content_service/flow/' + flow_id)
        setFlowId(flow_id)
      },
    }
  }, [gDriveFiles, gAuth, doTransfer, flowId, setFlowId])
}

export function useTransferJobFlowId() {
  const [flowId, setFlowId] = useUrlSearchParam(TRANSFER_FLOW_PARAM_KEY)
  const folderId = useAtomValue(presentationFolderAtom)

  useEffect(() => {
    setFlowId(undefined)
  }, [folderId, setFlowId])

  return [flowId, setFlowId] as const
}
